// Tamaños recomendados: cards / miniaturas / logos: 300 · detalle / modales: 800 · banners: 1200.
// (videos quedan fuera de este helper: solo se transforman URLs de imagen "image/upload")

// Solo URLs de imagen entregadas por el CDN de Cloudinary (res.cloudinary.com / res-N.cloudinary.com).
const CLOUDINARY_IMAGE_UPLOAD_RE =
  /^(https?:\/\/res(?:-\d+)?\.cloudinary\.com\/[^/?#]+\/image\/upload\/)([^?#]*)([?#].*)?$/i;

// Formatos que se pueden convertir/redimensionar con seguridad. Sin extensión también es válido.
// svg, gif (animados), pdf, ico, etc. se dejan intactos.
const OPTIMIZABLE_EXT_RE = /\.(jpe?g|png|webp|avif|bmp|tiff?|heic)$/i;

// Claves conocidas de parámetros de transformación de Cloudinary (para distinguir un segmento
// de transformación de una carpeta del public_id).
const TRANSFORM_KEYS = new Set([
  "a", "ac", "af", "ar", "b", "bl", "bo", "br", "c", "co", "cs", "d", "dl", "dn", "dpr", "du",
  "e", "eo", "f", "fl", "fn", "fps", "g", "h", "if", "ki", "l", "lo", "o", "p", "pg", "q", "r",
  "so", "sp", "t", "u", "vc", "vs", "w", "x", "y", "z",
]);

const SIGNATURE_SEGMENT_RE = /^s--[\w-]{8}--$/;
const VERSION_SEGMENT_RE = /^v\d+$/;

const isTransformationSegment = (segment) => {
  if (!segment) return false;
  return segment.split(",").every((part) => {
    if (part.startsWith("$")) return true; // variables de usuario
    const idx = part.indexOf("_");
    return idx > 0 && TRANSFORM_KEYS.has(part.slice(0, idx));
  });
};

const normalizeWidth = (width) => {
  const n = Math.round(Number(width));
  return Number.isFinite(n) && n > 0 ? n : 400;
};

const normalizeQuality = (quality) =>
  typeof quality === "string" && /^[\w.:]+$/.test(quality) ? quality : "auto";

/**
 * Optimiza (idempotente) una URL de imagen de Cloudinary agregando, solo si faltan,
 * f_auto, q_<quality> y w_<width>,c_limit como último paso de la cadena.
 * Cualquier otra entrada se devuelve sin cambios (o "" si no es texto).
 */
const optimizeCloudinaryUrl = (rawUrl, width, quality) => {
  const match = CLOUDINARY_IMAGE_UPLOAD_RE.exec(rawUrl);
  if (!match) return rawUrl;

  const [, prefix, path, tail = ""] = match;
  const segments = path.split("/");

  // URL firmada: cualquier cambio invalidaría la firma.
  if (SIGNATURE_SEGMENT_RE.test(segments[0])) return rawUrl;

  // public_id (con carpetas) + extensión + versión: se conservan tal cual.
  const versionIdx = segments.findIndex((s) => VERSION_SEGMENT_RE.test(s));
  let transformEnd;
  if (versionIdx >= 0) {
    transformEnd = versionIdx;
  } else {
    transformEnd = 0;
    while (transformEnd < segments.length - 1 && isTransformationSegment(segments[transformEnd])) {
      transformEnd += 1;
    }
  }
  const transformSegments = segments.slice(0, transformEnd);
  const rest = segments.slice(transformEnd);
  if (!rest.length || !rest[rest.length - 1]) return rawUrl;

  // Si los segmentos previos a la versión no son transformaciones reconocibles no se toca.
  if (!transformSegments.every(isTransformationSegment)) return rawUrl;

  if (!OPTIMIZABLE_EXT_RE.test(rest[rest.length - 1]) && /\.[a-z0-9]{2,5}$/i.test(rest[rest.length - 1])) {
    return rawUrl;
  }

  // Analiza solo los pasos "globales" (fuera de overlays/underlays).
  let inLayer = false;
  let hasFormat = false;
  let hasQuality = false;
  let minWidth = Infinity;
  transformSegments.forEach((segment) => {
    const parts = segment.split(",");
    const opens = parts.some((p) => p.startsWith("l_") || p.startsWith("u_"));
    const closes = parts.some((p) => p.startsWith("fl_") && p.includes("layer_apply"));
    const isLayerStep = inLayer || opens;
    if (!isLayerStep) {
      parts.forEach((p) => {
        if (p.startsWith("f_")) hasFormat = true;
        else if (p.startsWith("q_")) hasQuality = true;
        else if (/^w_\d+$/.test(p)) minWidth = Math.min(minWidth, Number(p.slice(2)));
      });
    }
    inLayer = (inLayer || opens) && !closes;
  });
  if (inLayer) return rawUrl; // capa sin cerrar: URL inusual, no se toca

  const additions = [];
  if (!hasFormat) additions.push("f_auto");
  if (!hasQuality) additions.push(`q_${quality}`);
  if (!(minWidth <= width)) additions.push(`w_${width}`, "c_limit");
  if (!additions.length) return rawUrl;

  return `${prefix}${[...transformSegments, additions.join(","), ...rest].join("/")}${tail}`;
};

export const buildCloudinaryImageUrl = ({
  publicId,
  url,
  width = 400, // Optional: Adjust the width as needed  cards : 300  modales 800 bannes 1200 videos 720
  quality = "auto",
  type = "image",
} = {}) => {
  const safeWidth = normalizeWidth(width);
  const safeQuality = normalizeQuality(quality);
  const safeUrl = typeof url === "string" ? url.trim() : "";
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

  if (typeof publicId === "string" && publicId.trim() && cloudName) {
    const id = publicId.trim().replace(/^\/+/, "");
    const transformation = `f_auto,q_${safeQuality},w_${safeWidth},c_limit`;
    return `https://res.cloudinary.com/${cloudName}/${type}/upload/${transformation}/${id}`;
  }

  // Sin publicId utilizable (o sin cloudName): se usa la URL disponible.
  if (!safeUrl) return "";
  return optimizeCloudinaryUrl(safeUrl, safeWidth, safeQuality);
};
